import React, { useState, useEffect } from "react";
import {
    Paper,
    Box,
    Switch,
    Typography,
    LinearProgress,
    FormControlLabel,
} from "@mui/material";
import SectionTitle from "../../common/SectionTitle";
import CurrentClientContext from "../../../providers/CurrentClientProvider";
import TipsContext from "../../../providers/GlobalTip";
import PosService from "../../../services/PosService";
import { trackEvent, handleTrackError } from "../../../services/AmplitudeService";
import constants from "../../../constants/constant";

function HeartlandHardMarkdownToggle() {
    const currentClient = CurrentClientContext.useClient();
    const tip = TipsContext.useMessage();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [enabled, setEnabled] = useState(null);

    const client = currentClient.client;
    const isHeartlandRetail =
        client && Number(client.PointOfSaleId) === constants.POS_ID.HEARTLAND_RETAIL_API;

    useEffect(() => {
        if (!isHeartlandRetail) return;
        let cancelled = false;
        async function load() {
            setLoading(true);
            try {
                const res = await PosService.getHeartlandHardMarkdownSetting(client.Id);
                if (cancelled) return;
                setEnabled(Boolean(res?.enabled));
            } catch (e) {
                if (cancelled) return;
                console.error(e);
                tip.show("Could not load hard markdown setting");
                handleTrackError({
                    error: e,
                    pageName: "Client POS Mapping",
                    additionalMetadata: {
                        clientCode: client?.ClientCode,
                        action: "Load Hard Markdown Setting",
                    },
                });
            }
            if (!cancelled) setLoading(false);
        }
        load();
        return () => {
            cancelled = true;
        };
    }, [client?.Id, isHeartlandRetail]);

    if (!isHeartlandRetail) return null;

    async function onToggle(e) {
        const next = e.target.checked;
        const previous = enabled;
        setEnabled(next);
        setSaving(true);
        try {
            await PosService.saveHeartlandHardMarkdownSetting(client.Id, next);
            tip.showSuccess(
                next
                    ? "Hard markdown capture turned on."
                    : "Hard markdown capture turned off."
            );
            trackEvent("Client POS Event", {
                action: "Toggle Heartland Hard Markdown Capture",
                clientCode: client?.ClientCode,
                enabled: next,
            });
        } catch (err) {
            setEnabled(previous);
            tip.show("Could not save setting");
            handleTrackError({
                error: err,
                pageName: "Client POS Mapping",
                additionalMetadata: {
                    clientCode: client?.ClientCode,
                    action: "Save Hard Markdown Setting",
                },
            });
        }
        setSaving(false);
    }

    return (
        <Paper elevation={2} sx={{ mt: 2 }}>
            <SectionTitle type="primary" label="Hard Markdown Capture" />
            <Box sx={{ p: 2 }}>
                {loading || saving ? (
                    <LinearProgress
                        sx={{
                            mb: 1.5,
                            borderRadius: 1,
                            height: 3,
                            backgroundColor: "rgba(92,63,117,0.1)",
                            "& .MuiLinearProgress-bar": { backgroundColor: "primary.main" },
                        }}
                    />
                ) : null}

                {enabled !== null && (
                    <FormControlLabel
                        control={
                            <Switch
                                checked={enabled}
                                onChange={onToggle}
                                disabled={loading || saving}
                                color="primary"
                            />
                        }
                        label={
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {enabled ? "Enabled" : "Disabled"}
                            </Typography>
                        }
                    />
                )}
            </Box>
        </Paper>
    );
}

export default HeartlandHardMarkdownToggle;
